import { useEffect, useRef, useState, useCallback } from 'react';

const SLIDES = [
  {
    image: 'https://storage.readdy-site.link/project_files/4acba870-6354-40bb-a23a-0b6bf3db091a/fe85b715-2d89-4683-9740-55f1f6774e44_KakaoTalk_20260326_121305616_03.jpg?v=d4f0e495c608ad92bf621b51831b2358',
    alt: '라인 인테리어 시공 사례 1',
  },
  {
    image: 'https://storage.readdy-site.link/project_files/4acba870-6354-40bb-a23a-0b6bf3db091a/6847e342-28e7-446b-80b8-6999ef940f3b_KakaoTalk_20260326_121305616_01.jpg?v=34472049106b7912413bcd53ff255b78',
    alt: '라인 인테리어 시공 사례 2',
  },
  {
    image: 'https://storage.readdy-site.link/project_files/4acba870-6354-40bb-a23a-0b6bf3db091a/e590fe64-fcd2-4be6-8373-85347b7da23e_KakaoTalk_20260326_121305616_02.jpg?v=f98a4c5a385e0a3143a7cfa383587a98',
    alt: '라인 인테리어 시공 사례 3',
  },
  {
    image: 'https://storage.readdy-site.link/project_files/4acba870-6354-40bb-a23a-0b6bf3db091a/e237c2b8-e985-43b1-9a66-30b7376c5fc5_KakaoTalk_20260326_121305616_04.jpg?v=d731c3ba779aca54f1a13332b7da710f',
    alt: '라인 인테리어 시공 사례 4',
  },
];

const STATS = [
  { value: '1,200+', label: '시공 완료' },
  { value: '98%', label: '고객 만족도' },
  { value: '15년+', label: '업계 경험' },
];

const INTERVAL = 4500;

export default function HeroBanner() {
  const [current, setCurrent] = useState(0);
  const [visible, setVisible] = useState(false);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  const next = useCallback(() => {
    setCurrent((prev) => (prev + 1) % SLIDES.length);
  }, []);

  const prev = useCallback(() => {
    setCurrent((prev) => (prev - 1 + SLIDES.length) % SLIDES.length);
  }, []);

  useEffect(() => {
    if (paused) return;
    timerRef.current = setInterval(next, INTERVAL);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [next, paused]);

  const handleDot = (idx: number) => {
    setCurrent(idx);
    if (timerRef.current) clearInterval(timerRef.current);
    if (!paused) timerRef.current = setInterval(next, INTERVAL);
  };

  const scrollTo = (id: string) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      className="w-full overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* ── 이미지 영역 ── */}
      <div
        className="hero-banner-wrap relative w-full overflow-hidden"
        style={{ height: '52vh', minHeight: '280px' }}
      >
        {/* 데스크탑 전용 높이 오버라이드 */}
        <style>{`
          @media (min-width: 768px) {
            .hero-banner-wrap { height: 82vh !important; min-height: 540px !important; max-height: 820px !important; }
          }
          @keyframes slideProgress { from { width: 0%; } to { width: 100%; } }
        `}</style>

        {/* 슬라이드 */}
        {SLIDES.map((slide, idx) => (
          <div
            key={slide.image}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              idx === current ? 'opacity-100 z-10' : 'opacity-0 z-0'
            }`}
          >
            <img src={slide.image} alt={slide.alt} className="w-full h-full object-cover object-center" />
          </div>
        ))}

        {/* 오버레이 */}
        <div className="absolute inset-0 z-20 bg-gradient-to-b from-black/40 via-black/35 to-black/65" />

        {/* 화살표 */}
        <button
          onClick={() => { prev(); if (timerRef.current) clearInterval(timerRef.current); timerRef.current = setInterval(next, INTERVAL); }}
          className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 z-30 w-9 h-9 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/40 backdrop-blur-sm text-white transition-colors cursor-pointer"
          aria-label="이전 슬라이드"
        >
          <i className="ri-arrow-left-s-line text-lg md:text-xl" />
        </button>
        <button
          onClick={() => { next(); if (timerRef.current) clearInterval(timerRef.current); timerRef.current = setInterval(next, INTERVAL); }}
          className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 z-30 w-9 h-9 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/40 backdrop-blur-sm text-white transition-colors cursor-pointer"
          aria-label="다음 슬라이드"
        >
          <i className="ri-arrow-right-s-line text-lg md:text-xl" />
        </button>

        {/* ── 모바일: 이미지 중앙에 헤드라인만 ── */}
        <div
          className={`md:hidden absolute inset-0 z-30 flex flex-col items-center justify-center text-center px-6 pt-16 transition-all duration-1000 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <span className="inline-block mb-3 px-3 py-1 rounded-full border border-white/50 text-white/90 text-xs tracking-widest font-medium uppercase">
            Paint · Coating · Waterproof
          </span>
          <h1 className="text-2xl font-bold text-white leading-tight">
            완벽한 마감,<br />
            <span className="text-blue-400">신뢰할 수 있는</span><br />
            전문 시공 파트너
          </h1>
        </div>

        {/* ── 데스크탑: 기존처럼 이미지 하단에 전체 콘텐츠 ── */}
        <div className="hidden md:flex absolute inset-0 z-30 flex-col justify-end">
          <div className="max-w-7xl mx-auto w-full px-8 pb-20">
            <div className={`transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <span className="inline-block mb-4 px-4 py-1.5 rounded-full border border-white/50 text-white/90 text-xs tracking-widest font-medium uppercase">
                Paint · Coating · Waterproof
              </span>
              <h1 className="text-5xl lg:text-6xl font-bold text-white leading-tight mb-4">
                완벽한 마감,<br />
                <span className="text-blue-400">신뢰할 수 있는</span><br />
                전문 시공 파트너
              </h1>
              <p className="text-base text-white/80 max-w-lg mb-7 leading-relaxed">
                라인 인테리어는 페인트, 도장, 방수 분야에서<br />
                15년 이상의 경험으로 고객의 공간을 안전하고<br />
                아름답게 완성합니다.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => scrollTo('#contact')}
                  className="flex items-center justify-center gap-2 px-7 py-3 rounded-md bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap"
                >
                  무료 견적 신청하기 <i className="ri-arrow-right-line" />
                </button>
                <button
                  onClick={() => scrollTo('#portfolio')}
                  className="flex items-center justify-center gap-2 px-7 py-3 rounded-md border-2 border-white/70 hover:bg-white/10 text-white font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap"
                >
                  시공 사례 보기 <i className="ri-gallery-line" />
                </button>
              </div>
            </div>

            {/* 데스크탑 Stats + Dots */}
            <div className={`mt-8 flex items-center justify-between transition-all duration-1000 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              <div className="flex rounded-xl overflow-hidden">
                {STATS.map((stat, idx) => (
                  <div
                    key={stat.label}
                    className={`min-w-[90px] bg-white/10 backdrop-blur-sm px-4 py-3 text-center ${idx < STATS.length - 1 ? 'border-r border-white/20' : ''}`}
                  >
                    <p className="text-2xl font-bold text-blue-400">{stat.value}</p>
                    <p className="text-xs text-white/80 mt-0.5">{stat.label}</p>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2">
                {SLIDES.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleDot(idx)}
                    className={`transition-all duration-300 rounded-full cursor-pointer ${idx === current ? 'bg-blue-400 w-6 h-2' : 'bg-white/40 hover:bg-white/70 w-2 h-2'}`}
                    aria-label={`슬라이드 ${idx + 1}`}
                  />
                ))}
                <span className="ml-2 text-white/60 text-xs">{current + 1} / {SLIDES.length}</span>
              </div>
            </div>
          </div>
        </div>

        {/* 모바일 슬라이드 닷 */}
        <div className="md:hidden absolute bottom-3 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2">
          {SLIDES.map((_, idx) => (
            <button
              key={idx}
              onClick={() => handleDot(idx)}
              className={`transition-all duration-300 rounded-full cursor-pointer ${idx === current ? 'bg-blue-400 w-5 h-1.5' : 'bg-white/50 w-1.5 h-1.5'}`}
              aria-label={`슬라이드 ${idx + 1}`}
            />
          ))}
        </div>

        {/* 프로그레스 바 */}
        {!paused && (
          <div className="absolute bottom-0 left-0 right-0 z-30 h-0.5 bg-white/10">
            <div
              key={current}
              className="h-full bg-blue-400"
              style={{ animation: `slideProgress ${INTERVAL}ms linear forwards` }}
            />
          </div>
        )}
      </div>

      {/* ── 모바일 전용 콘텐츠 패널 ── */}
      <div className={`md:hidden bg-stone-950 px-6 py-8 transition-all duration-1000 ${visible ? 'opacity-100' : 'opacity-0'}`}>
        <p className="text-white/70 text-sm leading-relaxed text-center mb-6">
          라인 인테리어는 페인트, 도장, 방수 분야에서<br />
          15년 이상의 경험으로 고객의 공간을 안전하고<br />
          아름답게 완성합니다.
        </p>

        <div className="flex flex-col gap-3 mb-7">
          <button
            onClick={() => scrollTo('#contact')}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-md bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap"
          >
            무료 견적 신청하기 <i className="ri-arrow-right-line" />
          </button>
          <button
            onClick={() => scrollTo('#portfolio')}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-md border border-white/30 hover:bg-white/10 text-white font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap"
          >
            시공 사례 보기 <i className="ri-gallery-line" />
          </button>
        </div>

        {/* 모바일 Stats */}
        <div className="flex rounded-xl overflow-hidden">
          {STATS.map((stat, idx) => (
            <div
              key={stat.label}
              className={`flex-1 bg-white/5 px-3 py-3 text-center ${idx < STATS.length - 1 ? 'border-r border-white/10' : ''}`}
            >
              <p className="text-xl font-bold text-blue-400">{stat.value}</p>
              <p className="text-xs text-white/60 mt-0.5">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
